import Quote from "../../../classes/Quote";
import SalesLead from "../../../classes/SalesLead";
import CustomerQuote from "../../../classes/CustomerQuote";
import { uid } from "quasar";
import { db, storage } from "../../../boot/firebase";
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
  uploadString,
} from "firebase/storage";
import { cloneDeep } from "lodash";
import { call, fetchBlob } from "../../../util/misc";
import { extFromDataURL } from "../../../util/imageUpload";

export const GET_QUOTE = async ({ dispatch }, { salesLeadID, quoteID }) => {
  let path = Quote.getPath(salesLeadID, quoteID);
  return dispatch("GET_DOC", {
    converter: Quote,
    path,
    stateKey: "quotes",
    id: quoteID,
  });
};

export const GET_QUOTES = (
  { dispatch },
  { salesLeadID, conditions, limitCount, pagination }
) => {
  return dispatch("GET_COLLECTION", {
    converter: Quote,
    stateKey: "quotes",
    path: salesLeadID ? Quote.getPath(salesLeadID) : null,
    conditions,
    limitCount,
    pagination,
  });
};

export const LISTEN_QUOTE = async (
  { dispatch },
  { salesLeadID, quoteID, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: Quote,
    path: Quote.getPath(salesLeadID, quoteID),
    stateKey: "quotes",
    id: quoteID,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_QUOTES = async (
  { dispatch },
  { salesLeadID, pagination, next, error, unlisten }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: Quote,
    path: Quote.getPath(salesLeadID),
    stateKey: "quotes",
    pagination,
    next,
    error,
    unlisten,
  });
};

export const SAVE_QUOTE = async ({ dispatch, state }, quote) => {
  const batch = writeBatch(db);

  const salesLeadID = quote.salesLeadID;

  const uploadImages = async () => {
    await Promise.all(
      quote.data.items.map(async (item) => {
        if (item.image && /^(data|blob):/.test(item.image.src)) {
          // Ensure image.ext is set; derive from dataURL MIME if missing so
          // we never generate filenames like "xxx.undefined".
          if (!item.image.ext) {
            item.image.ext = extFromDataURL(item.image.src);
          }
          // Upload local image
          if (!item.image.id) {
            // Create image id if none exists
            const suffix = item.image.ext ? "." + item.image.ext : "";
            item.image.id = uid().slice(-12) + suffix;
          }
          if (item.image.src.startsWith("blob:")) {
            // If blob URL, get the blob first
            let blob;
            try {
              blob = await fetchBlob(item.image.src);
            } catch (error) {
              throw "quoteImageUnavailable";
            }
            await uploadBytes(
              storageRef(storage, `${quote.path}/${item.image.id}`),
              blob
            );
          } else {
            // If data URL, upload directly
            await uploadString(
              storageRef(storage, `${quote.path}/${item.image.id}`),
              item.image.src,
              "data_url"
            );
          }
          item.image.src = await getDownloadURL(
            storageRef(storage, `${quote.path}/${item.image.id}`)
          );
        }
      })
    );
  };

  const saveDocuments = (existingData) => {
    return dispatch("SAVE_DOCUMENTS", {
      batch,
      parentData: quote.data,
      fieldPath: "attachments",
      folderPath: `${quote.path}/attachments`,
      existingData,
    });
  };

  // Handle Inventory Items
  const handleInventoryItems = async (existing) => {
    const items = quote.data.items.filter((item) => Boolean(item.inventoryID));
    if (items.length) {
      const now = new Date();
      await Promise.all(
        items.map(async (item) => {
          const inventoryItem = await dispatch(
            "GET_INVENTORY_ITEM",
            item.inventoryID
          );
          if (!item) {
            throw "inventoryItemNotFound";
          }
          if (
            inventoryItem.quantityAvailable < item.quantity &&
            (!(salesLeadID in inventoryItem.data.salesLeads) ||
              inventoryItem.data.salesLeads[salesLeadID].quantity +
                inventoryItem.quantityAvailable <
                item.quantity)
          ) {
            throw "inventoryItemUnavailable";
          }

          batch.update(doc(db, inventoryItem.path), {
            [`salesLeads.${salesLeadID}`]: {
              pendingSince: now,
              quantity: item.quantity,
            },
            modified: now,
            modifiedBy: state.user.id,
          });
        })
      );
    }
    if (existing) {
      const existingItems = existing.data.items.filter((item) =>
        Boolean(item.inventoryID)
      );
      const removed = existingItems.filter(
        (item) => !items.find((i) => i.id === item.id)
      );
      if (removed.length) {
        const now = new Date();
        await Promise.all(
          removed.map(async (item) => {
            const inventoryItem = await dispatch(
              "GET_INVENTORY_ITEM",
              item.inventoryID
            );
            if (!item) {
              throw "inventoryItemNotFound";
            }
            batch.update(doc(db, inventoryItem.path), {
              [`salesLeads.${salesLeadID}`]: deleteField(),
              modified: now,
              modifiedBy: state.user.id,
            });
          })
        );
      }
    }
  };

  if (!quote.id) {
    // New Quote
    quote.data.created = new Date();
    quote.data.createdBy = state.user.id;

    const ref = doc(collection(db, Quote.getPath(salesLeadID))).withConverter(
      Quote
    );
    quote.id = ref.id;

    await uploadImages();
    await saveDocuments();
    await handleInventoryItems();
    batch.set(ref, quote);
    await batch.commit();
  } else {
    // Existing Quote
    quote.data.modified = new Date();
    quote.data.modifiedBy = state.user.id;

    // Find images that need to be deleted
    const current = await dispatch("GET_QUOTE", {
      salesLeadID,
      quoteID: quote.id,
    });
    const deleted = [];
    current.data.items.forEach((item) => {
      if (item.image && item.image.id) {
        if (
          !quote.data.items.find((i) => i.image && i.image.id === item.image.id)
        ) {
          deleted.push(item.image.id);
        }
      }
    });
    // Delete removed images
    if (deleted.length) {
      await Promise.all(
        deleted.map((id) =>
          deleteObject(storageRef(storage, `${quote.path}/${id}`)).catch(
            (error) => console.error(error)
          )
        )
      );
    }
    await uploadImages();
    await saveDocuments(current.data);
    await handleInventoryItems(current);
    batch.set(doc(db, quote.path).withConverter(Quote), quote);
    await batch.commit();
  }
  return quote;
};

export const UPDATE_QUOTE_SETTINGS = async (
  { state },
  { salesLeadID, quoteID, settings }
) => {
  return updateDoc(doc(db, Quote.getPath(salesLeadID, quoteID)), {
    settings,
    modifiedBy: state.user.id,
  });
};

export const SEND_QUOTE = async (
  { dispatch, state },
  { quote, recipients, settings, attachments, subject, message }
) => {
  const wasSent = quote.data.status.sent && quote.data.status.sent.date;
  const batch = writeBatch(db);

  const salesLeadID = quote.salesLeadID;
  const salesLead = await dispatch("GET_SALES_LEAD", salesLeadID);
  const salesLeadRef = doc(db, salesLead.path).withConverter(SalesLead);
  const isChangeOrder = quote.data.status.original && salesLead.data.saleID;
  const originalQuoteRef = doc(db, quote.path).withConverter(Quote);
  if (!wasSent) {
    const customerQuote = await quote.toCustomerQuote(recipients, settings);
    customerQuote.data.created = new Date();
    customerQuote.data.createdBy = state.user.id;

    // Create customer quote
    const customerQuoteRef = doc(db, customerQuote.path).withConverter(
      CustomerQuote
    );

    // Save attachments
    if (attachments) {
      try {
        await dispatch("SAVE_DOCUMENTS", {
          batch,
          parentData: { attachments },
          fieldPath: "attachments",
          folderPath: `${quote.path}/attachments`,
          existingData: quote.data,
          parentRef: originalQuoteRef,
        });
        attachments = attachments.filter((doc) => doc && doc.file);
        customerQuote.data.attachments = attachments;
        attachments = cloneDeep(attachments).map((doc) => ({
          filename: doc.file.name,
          href: doc.file.url,
        }));
      } catch (error) {
        console.error(error);
        throw "failedUpdatingFiles";
      }
    }

    // Create customer quote
    batch.set(customerQuoteRef, customerQuote);

    // Update quote print settings and sent status
    batch.update(originalQuoteRef, {
      modifiedBy: state.user.id,
      "status.sent": {
        ...customerQuote.data.status.received,
        responseID: String(customerQuote.data.status.received.date.getTime()),
      },
      settings,
    });

    // Execute batch
    try {
      await batch.commit();
    } catch (error) {
      console.error(error);
      throw "failedCreatingCustomerQuote";
    }

    // If Change Order, update Sale status
    if (isChangeOrder) {
      await dispatch("UPDATE_SALE_STATUS", {
        projectID: salesLead.data.projectID,
        saleID: salesLead.data.saleID,
        changes: {
          [`status.changeOrders.${quote.id}`]: "pending",
        },
      });
    }
  }

  // Send email
  const customerEmails = recipients.map((to) =>
    `${state.contacts[to.contact]?.name || ""} <${to.email.address}>`.trim()
  );
  const salesPersonEmail = `${salesLead.status.assignedTo.name} <${salesLead.status.assignedTo.emails[0].address}>`;
  // For followups, transform attachments to email format (skip any with unsaved File objects)
  if (wasSent && attachments) {
    attachments = attachments
      .filter((doc) => doc && doc.file && doc.file.url)
      .map((doc) => ({
        filename: doc.file.name,
        href: doc.file.url,
      }));
  }
  const success = await dispatch("SEND_EMAIL", {
    to: customerEmails,
    cc: salesPersonEmail,
    replyTo: salesPersonEmail,
    subject,
    message,
    attachments,
  });
  if (success) {
    if (wasSent) {
      // Followup
      return call("quoteFollowup", {
        quoteID: quote.id,
        salesLeadID,
        recipients,
        date: new Date(),
      });
    } else {
      return Promise.all([
        updateDoc(salesLeadRef, {
          "status.outcome.type": isChangeOrder
            ? "pending-change-order"
            : "pending",
        }),
        updateDoc(originalQuoteRef, {
          "status.sent.delivered": new Date(),
          locked: !settings.nonfinal,
        }),
      ]);
    }
  }
};

export const DELETE_QUOTE = async (context, { salesLeadID, quoteID }) => {
  return deleteDoc(doc(db, Quote.getPath(salesLeadID, quoteID)));
};

export const GET_QUOTE_COLUMNS = async (context, brand) => {
  let snapshot = await getDoc(doc(db, `quote-columns/${brand}`));
  return snapshot ? snapshot.data() : null;
};

export const SET_QUOTE_COLUMNS = (context, { brand, columns }) => {
  return setDoc(doc(db, `quote-columns/${brand}`), columns);
};
